// src/contexts/AllocationContext.tsx
// FIXED VERSION: Properly includes 'both' semester allowances in load calculations

import React, { useEffect, useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { setLineAssignment } from '../services/lineAssignmentService';
import { useGrid } from './GridContext';
import { useData } from './DataContext';
import { supabase, DB_SCHEMA } from '../supabase/config';
import { ClassAllocation } from '../types/grid';
import { computeAvailableMinutes, computeUtilisation, computeAllocatedMinutes } from '../utils/teacherLoad';
import { useAcademicYear } from './AcademicYearContext';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT, DEFAULT_MINUTES_PER_LINE } from '../types/schoolConfig';
interface AllocationContextType {
  assignTeacher: (classId: string, lineId: string, teacherCode: string | null) => Promise<void>;
  updateClass: (classId: string, updates: Partial<ClassAllocation>) => Promise<void>;
  assignmentHistory: AssignmentHistoryEntry[];
  loading: boolean;
  error: string | null;
  getTeacherLoad: (teacherId: string) => TeachingLoad;
  getTeacherSemesterLoad: (teacherId: string, semester: 'semester1' | 'semester2') => SemesterTeachingLoad;
}
interface AssignmentHistoryEntry {
  id: string;
  classId: string;
  className: string;
  lineId: string;
  lineName?: string;
  teacherCode: string | null;
  teacherName: string | null;
  timestamp: Date;
  user: string;
  action: 'assign' | 'unassign';
}
interface TeachingLoad {
  allocated: number;
  maximum: number;
  overloaded: boolean;
  semester1: SemesterTeachingLoad;
  semester2: SemesterTeachingLoad;
}
interface SemesterTeachingLoad {
  allocatedMinutes: number;
  availableMinutes: number;
  utilization: number;
  overloaded: boolean;
}
const AllocationContext = createContext<AllocationContextType | undefined>(undefined);
export const AllocationProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const location = useLocation();
  // Allowances are year-scoped as of v2.11.0; the load calc below must filter by
  // the year on screen or it counts every year at once.
  const { workingYear } = useAcademicYear();
  
  // Disable this context on stabilized routes to prevent conflicts
  const stabilizedRoutes = ['/grid/editor', '/test/simple-grid-test'];
  const isStabilizedRoute = stabilizedRoutes.some(route => location.pathname.startsWith(route));
  
  if (isStabilizedRoute) {
    // Return a minimal context that won't interfere
    const minimalValue = {
      assignTeacher: async () => { },
      updateClass: async () => { },
      assignmentHistory: [],
      loading: false,
      error: null,
      getTeacherLoad: () => ({ allocated: 0, maximum: DEFAULT_BASE_MINUTES_PER_FORTNIGHT, overloaded: false, semester1: { allocatedMinutes: 0, availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT, utilization: 0, overloaded: false }, semester2: { allocatedMinutes: 0, availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT, utilization: 0, overloaded: false } }),
      getTeacherSemesterLoad: () => ({ allocatedMinutes: 0, availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT, utilization: 0, overloaded: false })
    };
    return <AllocationContext.Provider value={minimalValue}>{children}</AllocationContext.Provider>;
  }
  const {
    addToast
  } = useToast();
  const { user } = useAuth();
  const {
    activeGrid,
    updateGrid
  } = useGrid();
  const {
    staff
  } = useData();
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Disabled excessive logging - keeping system performant
  // DEBUG: Log context initialization and dependencies
  // useEffect(() => {
  //   console.log('🔶 AllocationContext: Context initialized with dependencies:', {
  //     activeGridId: activeGrid?.id,
  //     activeGridName: activeGrid?.name,
  //     staffCount: staff.length,
  //     updateGridFunction: typeof updateGrid,
  //     timestamp: new Date().toISOString()
  //   });
  // }, [activeGrid, staff, updateGrid]);

  // Set up Supabase listener for assignment history (only on non-stabilized routes)
  useEffect(() => {
    // Skip Supabase listeners on stabilized routes  
    const stabilizedRoutes = ['/grid/editor', '/test/simple-grid-test'];
    const isStabilizedRoute = stabilizedRoutes.some(route => location.pathname.startsWith(route));
    
    if (isStabilizedRoute) {
      setLoading(false);
      return;
    }

    // Fetch initial data
    const fetchAssignmentHistory = async () => {
      try {
        const { data, error } = await supabase
          .from('assignment_history')
          .select('*')
          .order('timestamp', { ascending: false });
          
        if (error) {
          console.error('❌ AllocationContext: Error fetching assignment history:', error);
          setError('Failed to fetch assignment history');
          addToast('Failed to fetch assignment history', 'error');
          setLoading(false);
          return;
        }

        const historyData = (data || []).map(item => ({
          id: item.id,
          classId: item.class_id,
          className: item.class_name,
          lineId: item.line_id,
          lineName: item.line_name,
          teacherCode: item.teacher_code,
          teacherName: item.teacher_name,
          timestamp: new Date(item.timestamp),
          user: item.user_name,
          action: item.action
        })) as AssignmentHistoryEntry[];
        
        setAssignmentHistory(historyData);
        setLoading(false);
      } catch (err) {
        console.error('❌ AllocationContext: Error fetching assignment history:', err);
        setError('Failed to fetch assignment history');
        addToast('Failed to fetch assignment history', 'error');
        setLoading(false);
      }
    };
    
    fetchAssignmentHistory();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('assignment-history-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'assignment_history'
        },
        (payload) => {
          fetchAssignmentHistory();
        }
      )
      .subscribe();

    // Cleanup listener
    return () => {
      subscription.unsubscribe();
    };
  }, [addToast, location.pathname]);

  // DEBUG: Enhanced assign teacher function with comprehensive logging
  const assignTeacher = async (classId: string, lineId: string, teacherCode: string | null) => {
    if (!activeGrid) {
      console.error('❌ AllocationContext: No active grid available for assignment');
      addToast('No active grid selected', 'error');
      return;
    }
    try {
      // Find the class and line
      const classItem = activeGrid.classes.find(c => c.id === classId);
      const line = activeGrid.lines.find(l => l.id === lineId);
      if (!classItem) {
        console.error('❌ AllocationContext: Class not found:', classId);
        throw new Error(`Class with ID ${classId} not found`);
      }
      if (!line) {
        console.error('❌ AllocationContext: Line not found:', lineId);
        throw new Error(`Line with ID ${lineId} not found`);
      }

      // Get teacher name if a teacher is being assigned
      let teacherName: string | null = null;
      if (teacherCode) {
        const teacher = staff.find(s => s.staffCode === teacherCode);
        teacherName = teacher?.fullName || teacherCode;
      }

      // Write only this cell, server-side. Rewriting the whole class array
      // from this browser's copy is what silently destroyed 212 assignments
      // in Sep 2026 (issue #218). The audit row is written by the same
      // transaction, so the previous insert here — which targeted columns
      // assignment_history does not have, and therefore always failed — is
      // gone with it.
      try {
        await setLineAssignment(
          activeGrid.id,
          {
            classId,
            lineId,
            teacherCode,
            teacherName,
            className: classItem.className,
            subject: classItem.subjectCode || classItem.className || null,
          },
          { email: (user as any)?.email, id: (user as any)?.id }
        );
      } catch (assignError) {
        console.error('❌ AllocationContext: assignment write failed:', {
          errorMessage: (assignError as any)?.message,
          gridId: activeGrid.id,
          classId,
          lineId,
        });
        throw assignError;
      }
      void line;
      const successMessage = teacherCode ? `Assigned ${teacherCode} to ${classItem.className} (Line ${lineId})` : `Unassigned teacher from ${classItem.className} (Line ${lineId})`;
      addToast(successMessage, 'success');
    } catch (err) {
      console.error('❌ AllocationContext: Error in assignTeacher:', err);
      console.error('❌ AllocationContext: Error details:', {
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        errorStack: err instanceof Error ? err.stack : 'No stack trace',
        classId,
        lineId,
        teacherCode
      });
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      addToast(`Assignment failed: ${errorMessage}`, 'error');
      throw err;
    }
  };

  // DEBUG: Enhanced update class function
  const updateClass = async (classId: string, updates: Partial<ClassAllocation>) => {
    if (!activeGrid) {
      console.error('❌ AllocationContext: No active grid available for class update');
      addToast('No active grid selected', 'error');
      return;
    }
    try {
      // Create updated classes array with the updates
      const updatedClasses = activeGrid.classes.map(c => {
        if (c.id === classId) {
          const updatedClass = {
            ...c,
            ...updates
          };
          return updatedClass;
        }
        return c;
      });

      // Update the grid using GridContext - this will handle Firestore update
      await updateGrid(activeGrid.id, {
        classes: updatedClasses
      });
      addToast('Class updated successfully', 'success');
    } catch (err) {
      console.error('❌ AllocationContext: Error updating class:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      addToast(`Update failed: ${errorMessage}`, 'error');
      throw err;
    }
  };

  // FIXED: Calculate teaching load for a teacher for a specific semester
  // Now properly includes allowances from the 'both' field
  const getTeacherSemesterLoad = (teacherId: string, semester: 'semester1' | 'semester2'): SemesterTeachingLoad => {
    if (!activeGrid || !staff) {
      return {
        allocatedMinutes: 0,
        availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        utilization: 0,
        overloaded: false
      };
    }

    // Find the staff member
    const staffMember = staff.find(s => s.id === teacherId || s.staffCode === teacherId);

    if (!staffMember) {
      return {
        allocatedMinutes: 0,
        availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        utilization: 0,
        overloaded: false
      };
    }

    // Get semester lines
    const semesterLines = activeGrid.lines.filter(line => line.studyPeriod === semester);
    const semesterLineIds = semesterLines.map(line => line.id);

    // Allocated minutes via the one shared definition (issue #93).
    //
    // This used to add a flat DEFAULT_MINUTES_PER_LINE per assigned line and
    // match a single teacher code, so it understated every class whose real
    // length differs from 440 and missed multi-teacher assignments entirely.
    // Scope is unchanged: still the active grid only.
    const allocatedMinutes = computeAllocatedMinutes(activeGrid, staffMember, semesterLineIds);

    // Available minutes via the one shared definition (issue #93).
    //
    // This used to sum semesterData.allowances and staffMember.both.allowances
    // inline, filtered only by appliesTo. That was correct until v2.11.0 made
    // allowances year-scoped and backfilled every record into both 2026 and
    // 2027 — after which the inline sum counted BOTH years and roughly doubled
    // every allowance. Staff Directory's Teaching Load column reads this, so it
    // showed too little available time and false overloads.
    //
    // getTotalAllowanceMinutes applies the same pro-rata (minutes x FTE) and the
    // same appliesTo rule, plus the year filter this was missing.
    const { availableMinutes } = computeAvailableMinutes(staffMember, semester, workingYear);
    const utilization = computeUtilisation(allocatedMinutes, availableMinutes);

    // Check if overloaded
    const overloaded = allocatedMinutes > availableMinutes;
    return {
      allocatedMinutes,
      availableMinutes,
      utilization,
      overloaded
    };
  };

  // Calculate combined teaching load for a teacher
  const getTeacherLoad = (teacherId: string): TeachingLoad => {
    const semester1Load = getTeacherSemesterLoad(teacherId, 'semester1');
    const semester2Load = getTeacherSemesterLoad(teacherId, 'semester2');

    // For compatibility with existing code, calculate a combined load
    // This will be used in places that haven't been updated to show per-semester loads
    const allocated = Math.round((semester1Load.allocatedMinutes + semester2Load.allocatedMinutes) / 2);
    const maximum = Math.round((semester1Load.availableMinutes + semester2Load.availableMinutes) / 2);
    const overloaded = semester1Load.overloaded || semester2Load.overloaded;
    return {
      allocated,
      maximum,
      overloaded,
      semester1: semester1Load,
      semester2: semester2Load
    };
  };
  const value = {
    assignTeacher,
    updateClass,
    assignmentHistory,
    loading,
    error,
    getTeacherLoad,
    getTeacherSemesterLoad
  };
  return <AllocationContext.Provider value={value}>
      {children}
    </AllocationContext.Provider>;
};
export const useAllocation = () => {
  const context = useContext(AllocationContext);
  if (context === undefined) {
    throw new Error('useAllocation must be used within an AllocationProvider');
  }

  // Disabled excessive logging - keeping system performant
  // DEBUG: Log what the hook is returning
  // console.log('🔶 AllocationContext: useAllocation hook called, returning:', {
  //   assignTeacherExists: !!context.assignTeacher,
  //   assignTeacherType: typeof context.assignTeacher,
  //   contextKeys: Object.keys(context)
  // });
  return context;
};